import { RasterImage } from "@ui/components/image/raster-image";
import { TextLink } from "@ui/components/text-link/text-link";
import { cx } from "@uxf/core/utils/cx";
import { ContentRenderer, RenderComponents, RenderImageComponentProps, WysiwygContent } from "@uxf/wysiwyg/Content";
import React, { FC, memo } from "react";

const WIDTH = 384;
const HEIGHT = 256;

const Image = memo<RenderImageComponentProps>(({ image }) => {
    if (!image.extension || !image.uuid) {
        return null;
    }

    const alt = image.alt || image.caption || "";
    const src = {
        uuid: image.uuid,
        namespace: image.namespace ?? null,
        extension: image.extension,
    };

    const img = (
        <RasterImage
            alt={alt}
            className="bg-primary-light-150 overflow-hidden rounded-xl"
            height={HEIGHT}
            mode="cover"
            src={src}
            width={WIDTH}
        />
    );

    const caption =
        image.caption || image.source ? (
            <figcaption className="mt-1 px-3">
                <p className="ui-typo-h4">{image.caption}</p>
                {image.caption && image.source ? <br /> : null}
                {image.source && <p className="ui-typo-small text-gray-muted">{image.source}</p>}
            </figcaption>
        ) : alt ? (
            <figcaption className="sr-only">{alt}</figcaption>
        ) : null;

    return (
        <figure className="mx-auto my-12 sm:max-w-sm">
            {img}
            {caption}
        </figure>
    );
});

Image.displayName = "Image";

interface Props {
    className?: string;
    components?: RenderComponents;
    data?: WysiwygContent;
}

export const WysiwygRenderer: FC<Props> = ({ className, components, data }) => (
    <div className={cx("_wysiwyg", className)}>
        {data ? (
            <ContentRenderer
                data={data}
                components={{
                    /* eslint-disable react/display-name */
                    boldComponent: ({ children }) => <strong>{children}</strong>,
                    bulletedListComponent: ({ children }) => <ul className="ml-6 list-disc">{children}</ul>,
                    h2Component: ({ children }) => <h2 className="ui-typo-h2">{children}</h2>,
                    h3Component: ({ children }) => <h3 className="ui-typo-h3">{children}</h3>,
                    h4Component: ({ children }) => <h4 className="ui-typo-h4">{children}</h4>,
                    imageComponent: Image,
                    italicComponent: ({ children }) => <em className="text-gray-muted">{children}</em>,
                    linkComponent: ({ children, link }) => (
                        <TextLink href={link.url} target={link.target} variant="orange">
                            {children}
                        </TextLink>
                    ),
                    listItemComponent: ({ children }) => <li>{children}</li>,
                    numberedListComponent: ({ children }) => <ol className="ml-6 list-decimal">{children}</ol>,
                    paragraphComponent: ({ children }) => <p>{children}</p>,
                    underlineComponent: ({ children }) => <u>{children}</u>,
                    videoWrapper: ({ children }) => (
                        <figure className="mx-auto my-12 sm:max-w-sm">
                            <div
                                className="bg-primary-light-150 aspect-w-16 aspect-h-9 flex items-center justify-center overflow-hidden rounded-xl bg-center bg-no-repeat"
                                style={{ backgroundImage: "url('/static/images/video-placeholder-icon.svg')" }}
                            >
                                {children}
                            </div>
                            <figcaption className="sr-only">Video</figcaption>
                        </figure>
                    ),
                    ...components,
                    /* eslint-enable react/display-name */
                }}
            />
        ) : null}
    </div>
);
